import { Badge, Button, Col, Input, InputNumber, Popconfirm, Row, Space, Switch, Table, message, Typography } from 'antd';
import { PageHeader } from "@ant-design/pro-layout";
import { DeleteFilled } from "@ant-design/icons";
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { updateUnitTestNote, deleteReferralFeeAsin, deleteUnitTestNote, getReferralFeeAsins, updateReferralFeeAsin, addReferralFeeAsin, calculateReferralFees, getUniteTestNoteById, getMyFeesEstimates } from 'services';
import { detectRegion, getIdFromObject, getUserIdUsingSPAPI } from 'utils';
import { Helmet } from 'react-helmet';

interface DataType {
  _id: string;
  asin: string;
  sellPrice: number;
  referFeeSPAPI: number;
  referFeeLibrary: number;
  closingFeeSPAPI: number | null;
  closingFeeLibrary: number | null;
  match: boolean;
}

const initInputAsin = {
  asin: null,
  sellPrice: 0,
}

const initRecord = {
  referFeeSPAPI: 0,
  referFeeLibrary: 0,
  closingFeeSPAPI: 0,
  closingFeeLibrary: 0,
  match: false
}

const ReferralFeeItem = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [activedTab, setActivedTab] = useState<string>("US")
  const [dataTable, setDataTable] = useState<any>([])
  const [userManagerId, setUserManagerId] = useState<string | null>(null)
  const [inputAsin, setInputAsin] = useState<any>(initInputAsin)
  const [userEmail, setUserEmail] = useState<string>('')
  const [loadingUser, setLoadingUser] = useState<boolean>(false)
  const [noteObj, setNoteObj] = useState<any>({
    categoryName: null,
    note: null
  })

  let { marketplace = "US", categoryId = "" } = useParams();
  let navigate = useNavigate();

  useEffect(() => {
    if (categoryId) {
      getUniteTestNoteById(categoryId)
        .then(({ data }: any) => {
          // Response structure: { status: "SUCCESS", data: {...} } (single object, not array)
          if (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
            setNoteObj(data.data)
          } else {
            setNoteObj(null)
          }
        })
        .catch(() =>
          setNoteObj(null)
        )

      getReferralFeeAsins(categoryId)
        .then(({ data }) => {
          // API now returns _id as string, no need to parse
          const asins = (data.data && Array.isArray(data.data)) ? data.data : []
          setDataTable(asins)
        })
        .catch(() =>
          message.error("Cannot get asins, please refresh page again")
        )
    }
  }, [activedTab])

  useEffect(() => {
    setActivedTab(marketplace)
  }, [marketplace])

  const handleOnBack = () => {
    navigate(`../${marketplace}`, { replace: true });
  };

  const handleUserEmailChange = (email: string) => {
    if (!email || email.trim() === '') {
      setUserManagerId(null)
      return
    }

    setLoadingUser(true)
    getUserIdUsingSPAPI(email.trim())
      .then((userId: string | null) => {
        if (userId) {
          setUserManagerId(userId)
          message.success(`User loaded: ${email}`)
        } else {
          setUserManagerId(null)
          message.warning(`User not found or does not have SP-token: ${email}`)
        }
      })
      .catch(() => {
        setUserManagerId(null)
        message.error(`Cannot get user info for: ${email}. Please check if user exists and has SP-token.`)
      })
      .finally(() => {
        setLoadingUser(false)
      })
  }

  const handleOnSaveCategory = () => {
    const query = {
      name: noteObj.categoryName,
      note: noteObj.note,
    }
    const id = noteObj._id // API now returns _id as string
    return updateUnitTestNote(id, query)
      .then(() => message.success('Updated category succesfully'))
      .catch(() => message.error('Cannot update category'))
  }

  const handleOnDeleteCategory = () => {
    return deleteUnitTestNote(categoryId)
      .then(() => handleOnBack)
      .catch(() => message.error('Cannot delete Category'))
  }

  const updateCalculateAsins = (items: any, updates: any, isNew: boolean = false) => {
    const query = items.map((i: any) => {
      const id = i._id // API now returns _id as string
      const referFeeLibrary =
        updates[id].referralFee !== undefined ? updates[id].referralFee : i.referFeeLibrary
      const referFeeSPAPI =
        updates[id]["ReferralFee"] !== undefined ? updates[id].ReferralFee.FinalFee.Amount : i.referFeeSPAPI
      const closingFeeLibrary =
        updates[id].closingFee !== undefined ? updates[id].closingFee : i.closingFeeLibrary
      const closingFeeSPAPI =
        updates[id]["VariableClosingFee"] !== undefined ? updates[id].VariableClosingFee.FinalFee.Amount : i.closingFeeSPAPI
      return ({
        ...i,
        _id: id,
        referFeeSPAPI,
        referFeeLibrary,
        closingFeeSPAPI,
        closingFeeLibrary,
        match:
          referFeeSPAPI === referFeeLibrary &&
          closingFeeSPAPI === closingFeeLibrary
      })
    })
    return updateReferralFeeAsin(categoryId, query)
      .then(() => {
        if (isNew) {
          message.success('Added asin succesfully')
        } else {
          message.success('Calculate asin(s) successfully')
        }
        setDataTable((prev: any) => {
          if (items.length === 1) {
            return prev.map((d: any) => {
              const id = query[0]._id
              const idData = d._id
              return idData === id ? { ...d, ...query[0] } : d
            })
          }
          return query
        })
        return true;
      })
      .catch(() => isNew ? message.error('Can not add asin') : message.error('Can not update asins'))
      .finally(() => setLoading(false))
  }

  const handleOnAddAndCalculateAsin = async (item: any) => {
    setLoading(true)
    const data = {
      ...initRecord,
      ...item,
    }
    return addReferralFeeAsin(categoryId, data)
      .then(({ data }) => {
        // API now returns _id as string
        const newAsin = data.data
        setDataTable((prev: any) => ([newAsin, ...prev]))
        handleOnCalculateAsins([newAsin], true)
        return true;
      })
      .catch(() => message.error('Can not add asin'))
      .finally(() => setInputAsin(initInputAsin))
  }

  const handleOnCalculateAsins = async (items: any, isNew: boolean = false) => {
    const [feeSPAPI, feeLibrary] = await Promise.all([handleOnRunSpApi(items), hanldeOnRunLibrary(items)])
    const newAsins: any = []
    items.map((i: any) => {
      const id = i._id // API now returns _id as string
      newAsins[id] = {
        ...i,
        ...feeSPAPI[id],
        ...feeLibrary[id],
        _id: id,
      }
    })
    return updateCalculateAsins(items, newAsins, isNew)
  }

  const handleOnRunSpApi = (items: any, isUpdate: boolean = false) => {
    setLoading(true)

    const region = detectRegion(marketplace as string)
    const list: [] = items.reduce(
      (prev: any, current: any) => [
        ...prev,
        {
          id: current._id, // API now returns _id as string
          asin: current.asin,
          isAmazonFulfilled: false,
          price: current.sellPrice
        }
      ], [])

    const params = {
      marketplace,
      region,
      userId: userManagerId,
      fieldReturn: ['ReferralFee', 'VariableClosingFee'],
      list
    }

    return getMyFeesEstimates(params)
      .then(({ data }: any) => {
        if (!isUpdate) {
          return data.fees
        }

        return updateCalculateAsins(items, data.fees)
      })
      .catch(() => {
        message.error('Can not calculate by SP-API');
      })
  }

  const hanldeOnRunLibrary = (items: any, isUpdate: boolean = false) => {
    setLoading(true)

    const params = items.map((i: any) => ({
      asin: i.asin,
      _id: i._id, // API now returns _id as string
      price: i.sellPrice,
    }))
    return calculateReferralFees(marketplace, noteObj.categoryName, params)
      .then(({ data }: any) => {
        if (!isUpdate) {
          return data
        }
        return updateCalculateAsins(items, data)
      }).catch(() => message.error('Can not calculate by Library'))
  };

  const handleOnDeleteAsin = (id: string) => {
    return deleteReferralFeeAsin(id)
      .then(() => {
        const newData = dataTable.filter((d: any) => d._id !== id);
        setDataTable(newData);
        message.success("Deleted ASIN successfully")
      })
      .catch(() => message.error("Cannot delete ASIN"))
  }

  const columns: ColumnsType<DataType> = [
    {
      title: 'ASIN',
      dataIndex: 'asin',
      key: 'asin',
      render: text => <a>{text}</a>,
    },
    {
      title: 'Sell price',
      dataIndex: 'sellPrice',
      key: 'sellPrice',
    },
    {
      title: 'ReferFee SP-API',
      dataIndex: 'referFeeSPAPI',
      key: 'referFeeSPAPI',
    },
    {
      title: 'ReferFee library',
      dataIndex: 'referFeeLibrary',
      key: 'referFeeLibrary',
    },
    {
      title: 'ClosingFee SP-API',
      dataIndex: 'closingFeeSPAPI',
      key: 'closingFeeSPAPI',
    },
    {
      title: 'ClosingFee library',
      dataIndex: 'closingFeeLibrary',
      key: 'closingFeeLibrary',
    },
    {
      title: 'Match',
      dataIndex: 'match',
      key: 'match',
      render: value => (
        <Badge
          text={value ? "Yes" : "No"}
          status={value ? "success" : "error"}
        />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button type="primary" onClick={() => handleOnRunSpApi([record], true)} disabled={!userManagerId}>SP-API</Button>
          <Button type="default" onClick={() => hanldeOnRunLibrary([record], true)} disabled={!userManagerId}>Library</Button>
          <Button type="primary" onClick={() => handleOnCalculateAsins([record])} disabled={!userManagerId}>Both</Button>
          <Popconfirm
            placement="topRight"
            title="Are you sure to delete this ASIN?"
            onConfirm={() => handleOnDeleteAsin(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" icon={<DeleteFilled />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>{`${noteObj.categoryName} | Referral Fee | Unit Test`}</title>
      </Helmet>
      <PageHeader
        className="page-header"
        onBack={handleOnBack}
        title="Unit Test for Referral Fee - ASINs"
        footer={
          <Space direction="vertical" size="large" style={{ width: '100%', marginTop: 30 }}>
            <Input.Group>
              <Row gutter={8} align="middle">
                <Col span={4}>
                  <Typography.Text strong>User Email (with SP-token):</Typography.Text>
                </Col>
                <Col span={10}>
                  <Input
                    value={userEmail}
                    onChange={(e) => {
                      setUserEmail(e.target.value)
                      setUserManagerId(null) // Reset userId when email changes
                    }}
                    onPressEnter={(e) => handleUserEmailChange((e.target as HTMLInputElement).value)}
                    onBlur={(e) => handleUserEmailChange(e.target.value)}
                    placeholder="Enter user email with SP-token"
                    disabled={loadingUser}
                  />
                </Col>
                <Col span={4}>
                  {loadingUser ? (
                    <Typography.Text type="secondary">Loading...</Typography.Text>
                  ) : userManagerId ? (
                    <Typography.Text type="success">✓ User loaded</Typography.Text>
                  ) : (
                    <Typography.Text type="warning">⚠ Please enter valid user email</Typography.Text>
                  )}
                </Col>
              </Row>
            </Input.Group>
            <Input.Group>
              <Row gutter={8}>
                <Col span={4}>
                  <label>Category name:</label>
                </Col>
                <Col span={10}>
                  <label>Note:</label>
                </Col>
              </Row>
              <Row gutter={8}>
                <Col span={4}>
                  <Input value={noteObj.categoryName} onChange={(e) => setNoteObj((prev: any) => ({ ...prev, categoryName: e.target.value }))} />
                </Col>
                <Col span={12}>
                  <Input.TextArea value={noteObj.note} onChange={(e) => setNoteObj((prev: any) => ({ ...prev, note: e.target.value }))} />
                </Col>
                <Col span={4} offset={1}>
                  <Space align="end">
                    <Button type="primary" onClick={handleOnSaveCategory}>Save</Button>
                    <Button danger onClick={handleOnDeleteCategory}>Delete</Button>
                  </Space>
                </Col>
              </Row>
            </Input.Group>
            <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Space>
                <Input.Group compact>
                  <Input
                    value={inputAsin.asin || undefined}
                    style={{ width: 200 }}
                    placeholder="Enter ASIN (require)"
                    onChange={(e: any) => setInputAsin({ ...inputAsin, asin: e.target.value })}
                  />
                  <InputNumber
                    value={inputAsin.sellPrice || null}
                    controls={false}
                    style={{ width: 200 }}
                    placeholder="Sell price"
                    onChange={(value) => setInputAsin({ ...inputAsin, sellPrice: value })}
                  />
                  <Button type="primary" onClick={() => handleOnAddAndCalculateAsin(inputAsin)} disabled={!userManagerId}>Calculate Referral Fee</Button>
                </Input.Group>
                <Button onClick={() => handleOnCalculateAsins(dataTable)} disabled={!userManagerId}>Test all ASINs</Button>
              </Space>
            </Space>
            <Table columns={columns} dataSource={dataTable} loading={loading} scroll={{ x: true }} />
          </Space>
        }
      />
    </>
  )
};

export default ReferralFeeItem;